import AdminDateRange from '../AdminDateRange/AdminDateRange';
import AdminState from '../AdminState';
import { AdminAlertIcon as AlertIcon, RepeatIcon, InfoIcon } from '../AdminIcon';
// Components/Tabs/LabStatisticsTab.jsx

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Slider from "../Slider/Slider";
import { AdminToolbar } from "../AdminLayout/AdminLayout";
import Backend from "../../../utils/api.js";
import { Box, Text, VStack, HStack, Badge, Button, Alert, IconButton, Stat, StatLabel, StatNumber, StatHelpText, SimpleGrid, Progress, Tabs, TabList, TabPanels, Tab, TabPanel, Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react';

import AdminUserDetailsTab from "./AdminUserDetailsTab";
import "./LabStatisticsTab.css";

/**
 * LabStatisticsTab Component
 * 
 * Displays lab usage statistics including total sessions, unique users,
 * machine usage, and daily breakdown for a selected time period.
 */
function LabStatisticsTab({ user, labId, pushTab, revert }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ preset: 'last7', start: null, end: null });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    let isCancelled = false;

    const loadStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await Backend.adminGetLabStatistics(
          user.UID,
          labId,
          'week',
          dateRange.start,
          dateRange.end
        );

        // Only update state if this request hasn't been cancelled
        if (!isCancelled) {
          if (response.status === 200) {
        setHasLoaded(true);
            setStats(response);
          } else if (response.status === 403) {
            setError("You do not have permission to view statistics");
          } else if (response.status === 404) {
            setError("Space not found");
          } else {
            setError("Failed to load statistics");
          }
          setLoading(false);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error("Error loading statistics:", err);
          setError("Failed to connect to the server");
          setLoading(false);
        }
      }
    };

    loadStatistics();

    // Cleanup function to cancel in-flight requests
    return () => {
      isCancelled = true;
    };
  }, [labId, dateRange, user.UID, refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    // Parse date string as local date to avoid timezone conversion issues
    // Backend sends dates in format "YYYY-MM-DD" which should be treated as local dates
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const formatDateRange = (startStr, endStr) => {
    if (!startStr || !endStr) return "";
    const start = new Date(`${startStr.slice(0, 10)}T00:00:00`);
    const end = new Date(`${endStr.slice(0, 10)}T00:00:00`);
    const startFormatted = start.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
    const endFormatted = end.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
    return `${startFormatted} - ${endFormatted}`;
  };

  const getMaxMachineCount = () => {
    if (!stats?.top_tools?.length) return 1;
    return Math.max(...stats.top_tools.map(m => m.count));
  };

  const getMaxDailyCount = () => {
    if (!stats?.daily_breakdown?.length) return 1;
    return Math.max(...stats.daily_breakdown.map(d => d.sessions));
  };
  const getMaxHourCount = () => {
    if (!stats?.activity_histogram?.by_hour?.length) return 1;
    return Math.max(...stats.activity_histogram.by_hour, 1);
  };
  const getMaxDayOfWeekCount = () => {
    if (!stats?.activity_histogram?.by_day_of_week?.length) return 1;
    return Math.max(...stats.activity_histogram.by_day_of_week, 1);
  };
  const getReasonPercent = (count) => {
    if (!stats?.reason_breakdown?.length) return 0;
    const total = stats.reason_breakdown.reduce((sum, r) => sum + r.count, 0);
    return total > 0 ? Math.round((count / total) * 100) : 0;
  };

  const handleViewUserInfo = (activeUser) => {
    // Create a targetUser object from the active user data
    const targetUser = {
      UID: activeUser.user_id,
      firstName: activeUser.first_name,
      lastName: activeUser.last_name,
      email: activeUser.email || "",
      directoryID: "", // Will be loaded in AdminUserDetailsTab
    };
    
    pushTab(
      <AdminUserDetailsTab adminRoute="user"
        key={`adminUserDetails-${activeUser.UID}`}
        user={user}
        targetUser={targetUser}
        labId={labId}
        pushTab={pushTab}
        revert={revert}
      />
    );
  };

  return (
    <Slider
      name="Space statistics"
      navColor="rgb(220, 53, 69)"
      slide={true}
      index={0}
      onClick={() => revert(1)}
    >
      <div className={`LabStatisticsTab${loading && hasLoaded ? ' admin-refreshing' : ''}`} aria-busy={loading}>
        <AdminToolbar title={`${labId} - Statistics`} subtitle={stats?.period ? formatDateRange(stats.period.start, stats.period.end) : null}>
          <IconButton icon={<RepeatIcon />} onClick={handleRefresh} aria-label="Refresh statistics" isLoading={loading} variant="outline" />
        </AdminToolbar>

        <Box className="period-section" mb={4}>
          <AdminDateRange value={dateRange} onChange={setDateRange} />
        </Box>

        {/* Error Display */}
        {error && (
          <AdminState status="error" title="Statistics could not be loaded" description={error} onRetry={handleRefresh} />
        )}

        {/* Loading State */}
        {loading && !hasLoaded ? (
          <AdminState status="loading" title="Loading statistics…" />
        ) : error ? null : stats ? (
          <div className="stats-content">
            {/* Summary Stats */}
            <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={6}>
              <Box className="stat-card">
                <Stat>
                  <StatLabel>Total sessions</StatLabel>
                  <StatNumber>{stats.summary?.total_sessions || 0}</StatNumber>
                  <StatHelpText>Check-ins</StatHelpText>
                </Stat>
              </Box>

              <Box className="stat-card">
                <Stat>
                  <StatLabel>Unique users</StatLabel>
                  <StatNumber>{stats.summary?.unique_users || 0}</StatNumber>
                  <StatHelpText>Different people</StatHelpText>
                </Stat>
              </Box>

              <Box className="stat-card">
                <Stat>
                  <StatLabel>Total hours</StatLabel>
                  <StatNumber>{stats.summary?.total_hours || 0}</StatNumber>
                  <StatHelpText>Space usage</StatHelpText>
                </Stat>
              </Box>

              <Box className="stat-card">
                <Stat>
                  <StatLabel>Average session</StatLabel>
                  <StatNumber>{stats.summary?.avg_session_minutes || 0}m</StatNumber>
                  <StatHelpText>Per visit</StatHelpText>
                </Stat>
              </Box>

            <Box className="stat-card">
              <Stat>
                <StatLabel>Repeat / first-time visitors</StatLabel>
                <StatNumber>{stats.summary?.repeat_visitors || 0}:{stats.summary?.first_time_visitors || 0}</StatNumber>
                <StatHelpText>People</StatHelpText>
              </Stat>
            </Box>
          </SimpleGrid>

            {/* Automatic checkouts */}
            {stats.summary?.auto_logouts > 0 && (
              <Alert status="info" mb={4} borderRadius="md">
                <AlertIcon status="info" />
                <Text fontSize="sm">
                  {stats.summary.auto_logouts} session{stats.summary.auto_logouts !== 1 ? "s were" : " was"} automatically checked out
                </Text>
              </Alert>
            )}

            {/* Check outs */}
            {stats.summary?.force_logouts > 0 && (
              <Alert status="warning" mb={4} borderRadius="md">
                <AlertIcon status="warning" />
                <Text fontSize="sm">
                  {stats.summary.force_logouts} session{stats.summary.force_logouts !== 1 ? "s were" : " was"} checked out by an admin
                </Text>
              </Alert>
            )}

            <Tabs variant="enclosed" colorScheme="blue" mb={6}>
              <TabList>
                <Tab>Overview</Tab>
                <Tab>Most active users</Tab>
                <Tab>Activity heatmap</Tab>
              </TabList>

              <TabPanels>
                {/* Overview Tab */}
                <TabPanel>
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                    {/* Top Machines */}
                    <Box className="section-card">
                      <Text as="h3" fontWeight="semibold" fontSize="xl" mb={3}>Top tools</Text>
                      {stats.top_tools?.length > 0 ? (
                        <VStack align="stretch" spacing={2}>
                          {stats.top_tools.map((machine, idx) => (
                            <Box key={idx}>
                              <HStack justify="space-between" mb={1}>
                                <Text fontSize="sm" fontWeight="medium">
                                  {machine.name}
                                </Text>
                                <HStack spacing={2}>
                                  <Badge colorScheme="teal">{machine.count}</Badge>
                                  {machine.avg_duration_minutes != null && (
                                    <Badge colorScheme="purple">
                                      avg {machine.avg_duration_minutes} min
                                    </Badge>
                                  )}
                                </HStack>
                              </HStack>
                              <Progress
                                aria-label={`${machine.name}: ${machine.count} uses`}
                                value={(machine.count / getMaxMachineCount()) * 100}
                                size="sm"
                                colorScheme="teal"
                                borderRadius="full"
                              />
                            </Box>
                          ))}
                        </VStack>
                      ) : (
                        <Text fontSize="sm" color="gray.600">
                          No tool instance usage data
                        </Text>
                      )}


                    </Box>

                    {/* Daily Breakdown */}
                    <Box className="section-card">
                      <Text as="h3" fontWeight="semibold" fontSize="xl" mb={3}>Daily activity</Text>
                      {stats.daily_breakdown?.length > 0 ? (
                        <VStack align="stretch" spacing={2} className="daily-breakdown">
                          {stats.daily_breakdown.map((day, idx) => (
                            <Box key={idx}>
                              <HStack justify="space-between" mb={1}>
                                <Text fontSize="sm" fontWeight="medium">
                                  {formatDate(day.date)}
                                </Text>
                                <HStack spacing={2}>
                                  <Badge colorScheme="blue" size="sm">
                                    {day.sessions} session{day.sessions !== 1 ? "s" : ""}
                                  </Badge>
                                  <Badge colorScheme="green" size="sm">
                                    {day.unique_users} user{day.unique_users !== 1 ? "s" : ""}
                                  </Badge>
                                </HStack>
                              </HStack>
                              <Progress
                                aria-label={`${formatDate(day.date)}: ${day.sessions} sessions, ${day.unique_users} users`}
                                value={(day.sessions / getMaxDailyCount()) * 100}
                                size="sm"
                                colorScheme="blue"
                                borderRadius="full"
                              />
                            </Box>
                          ))}
                        </VStack>
                      ) : (
                        <Text fontSize="sm" color="gray.600">
                          No daily data available
                        </Text>
                      )}
                    </Box>

                    {/* Check-in purposes */}
                    <Box className="section-card">
                      <Text as="h3" fontWeight="semibold" fontSize="xl" mb={3}>Check-in purposes</Text>
                      {stats.reason_breakdown?.length > 0 ? (
                        <VStack align="stretch" spacing={2}>
                          {stats.reason_breakdown.map((r, idx) => (
                            <Box key={idx}>
                              <HStack justify="space-between" mb={1}>
                                <Text fontSize="sm" fontWeight="medium">
                                  {r.reason}
                                </Text>
                                <Badge colorScheme="purple">{getReasonPercent(r.count)}%</Badge>
                              </HStack>
                              <Progress
                                aria-label={`${r.reason}: ${r.count} sessions (${getReasonPercent(r.count)}%)`}
                                value={getReasonPercent(r.count)}
                                size="sm"
                                colorScheme="purple"
                                borderRadius="full"
                              />
                            </Box>
                          ))}
                        </VStack>
                      ) : (
                        <Text fontSize="sm" color="gray.600">
                          No check-in purpose data
                        </Text>
                      )}
                    </Box>
                    
                  </SimpleGrid>
                </TabPanel>

                {/* Most active users Tab */}
                <TabPanel>
                  <Box className="section-card">
                    <Text as="h3" fontWeight="semibold" fontSize="xl" mb={3}>Most active users</Text>
                    {stats.most_active_users?.length > 0 ? (
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Name</Th>
                            <Th isNumeric>Check-ins</Th>
                            <Th isNumeric>Time Spent (hours)</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {stats.most_active_users.map((activeUser, idx) => (
                            <Tr key={idx}>
                              <Td>
                                <VStack align="start" spacing={0}>
                                  <Text fontWeight="medium">
                                    {activeUser.first_name} {activeUser.last_name}
                                  </Text>
                                  {activeUser.email && (
                                    <Text fontSize="xs" color="gray.600">
                                      {activeUser.email}
                                    </Text>
                                  )}
                                </VStack>
                              </Td>
                              <Td isNumeric>
                                <Badge colorScheme="blue" fontSize="sm">
                                  {activeUser.sessions}
                                </Badge>
                              </Td>
                              <Td isNumeric>
                                <Badge colorScheme="green" fontSize="sm">
                                  {activeUser.total_hours}h
                                </Badge>
                              </Td>
                              <Td>
                                <Button
                                  size="sm"
                                  leftIcon={<InfoIcon />}
                                  colorScheme="blue"
                                  variant="outline"
                                  onClick={() => handleViewUserInfo(activeUser)}
                                >
                                  Info
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    ) : (
                      <Text fontSize="sm" color="gray.600">
                        No active users found for this time period
                      </Text>
                    )}
                  </Box>
                </TabPanel>

                {/* Activity heatmap Tab */}
                <TabPanel>
                  <VStack align="stretch" spacing={6}>
                    <Box className="section-card">
                      <Text as="h3" fontWeight="semibold" fontSize="xl" mb={3}>Activity by Hour of Day</Text>
                      {stats.activity_histogram?.by_hour ? (
                        <Box overflowX="auto" tabIndex={0} role="region" aria-label="Hourly activity; scroll horizontally to see all hours"
                          _focusVisible={{ outline: '2px solid', outlineColor: 'blue.500', outlineOffset: '2px' }}>
                          <HStack spacing={1} align="flex-end" minW="576px">
                          {stats.activity_histogram.by_hour.map((count, hour) => (
                            <VStack key={hour} spacing={1} flex="1">
                              <Box
                                w="100%"
                                h="60px"
                                bg={count > 0 ? "red.400" : "gray.100"}
                                opacity={count > 0 ? Math.min(0.3 + (count / getMaxHourCount()) * 0.7, 1) : 1}
                                borderRadius="sm"
                              />
                              <Text fontSize="xs" color="gray.600">{hour}</Text>
                            </VStack>
                          ))}
                        </HStack></Box>
                      ) : (
                        <Text fontSize="sm" color="gray.600">
                          No hourly data available
                        </Text>
                      )}
                    </Box>

                    <Box className="section-card">
                      <Text as="h3" fontWeight="semibold" fontSize="xl" mb={3}>Activity by Day of Week</Text>
                      {stats.activity_histogram?.by_day_of_week ? (
                        <HStack spacing={2} align="flex-end">
                          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label, idx) => {
                            const count = stats.activity_histogram.by_day_of_week[idx];
                            return (
                              <VStack key={idx} spacing={1} flex="1">
                                <Box
                                  w="100%"
                                  h="60px"
                                  bg={count > 0 ? "blue.400" : "gray.100"}
                                  opacity={count > 0 ? Math.min(0.3 + (count / getMaxDayOfWeekCount()) * 0.7, 1) : 1}
                                  borderRadius="sm"
                                />
                                <Text fontSize="xs" color="gray.600">{label}</Text>
                              </VStack>
                            );
                          })}
                        </HStack>
                      ) : (
                        <Text fontSize="sm" color="gray.600">
                          No day-of-week data available
                        </Text>
                      )}
                    </Box>
                  </VStack>
                </TabPanel>
                
              </TabPanels>
            </Tabs>
          </div>
        ) : (
          <AdminState title="No statistics available" description="Visit and tool usage statistics will appear here once this space has activity." />
        )}
      </div>
    </Slider>
  );
}

LabStatisticsTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default LabStatisticsTab;
